(function () {
  'use strict';

  const BAR_COUNT = 120;
  const SVG_WIDTH = 1000;
  const SVG_HEIGHT = 46;
  const SVG_CENTER = SVG_HEIGHT / 2;

  const CACHE_VERSION = 'ocular-waveform-native-v1';


  /* =========================================================
     ARTWORK
     ========================================================= */

  function applyArtworkAspectClass(image) {
    if (!image) return;

    function update() {
      if (!image.naturalWidth || !image.naturalHeight) return;

      const ratio =
        image.naturalWidth /
        image.naturalHeight;

      const isSquare =
        ratio >= 0.90 &&
        ratio <= 1.10;

      image.classList.toggle(
        'is-square',
        isSquare
      );
    }

    if (
      image.complete &&
      image.naturalWidth
    ) {
      update();
    } else {
      image.addEventListener(
        'load',
        update,
        { once: true }
      );
    }
  }


  /* =========================================================
     INITIALIZE PLAYER
     ========================================================= */

  function initializeOcularPlayer(player) {
    if (!player) return;

    if (player.dataset.initialized === 'true') {
      return;
    }

    player.dataset.initialized = 'true';


    const audio =
      player.querySelector('[data-audio]');

    const playlist =
      player.querySelector('[data-playlist]');

    const tracks =
      Array.from(
        player.querySelectorAll('[data-track]')
      );

    const activeArtwork =
      player.querySelector('[data-active-artwork]');

    const activeArtworkWrap =
      player.querySelector('[data-active-artwork-wrap]');

    const activeTitle =
      player.querySelector('[data-active-title]');

    const activeSubtitle =
      player.querySelector('[data-active-subtitle]');

    const currentTimeEl =
      player.querySelector('[data-current-time]');

    const durationEl =
      player.querySelector('[data-duration]');

    const waveform =
      player.querySelector('[data-waveform]');

    const svg =
      player.querySelector('[data-wave-svg]');

    const playButton =
      player.querySelector('[data-play]');

    const playIcon =
      player.querySelector('[data-play-icon]');

    const pauseIcon =
      player.querySelector('[data-pause-icon]');

    const previousButton =
      player.querySelector('[data-previous]');

    const nextButton =
      player.querySelector('[data-next]');

    const loopButton =
      player.querySelector('[data-loop]');

    const volumeButton =
      player.querySelector('[data-volume]');

    const volumeOn =
      player.querySelector('[data-volume-on]');

    const volumeOff =
      player.querySelector('[data-volume-off]');


    if (
      !audio ||
      !tracks.length ||
      !svg ||
      !waveform
    ) {
      return;
    }


    /*
      Important:
      no audio should preload before customer interaction.
    */

    audio.preload = 'none';


    let activeIndex = 0;
    let bars = [];
    let analysisRequestId = 0;
    let loadedAudioUrl = '';


    /* =========================================================
       SINGLE / MULTI TRACK MODE
       ========================================================= */

    const isSingleTrack =
      tracks.length === 1;

    player.classList.toggle(
      'is-single-track',
      isSingleTrack
    );


    /* =========================================================
       PLAYLIST ARTWORK ASPECT DETECTION
       ========================================================= */

    player
      .querySelectorAll(
        '.ocular-player__playlist-artwork'
      )
      .forEach(function (image) {
        applyArtworkAspectClass(image);
      });


    /* =========================================================
       TIME
       ========================================================= */

    function formatTime(seconds) {
      if (!Number.isFinite(seconds)) {
        return '0:00';
      }

      const minutes =
        Math.floor(seconds / 60);

      const secs =
        Math.floor(seconds % 60)
          .toString()
          .padStart(2, '0');

      return minutes + ':' + secs;
    }


    /* =========================================================
       REALISTIC PLACEHOLDER WAVEFORM

       Deterministic instead of random so it remains visually
       stable while looking much closer to genuine audio.
       ========================================================= */

    function createPlaceholderWaveform() {
      const data = [];

      for (
        let i = 0;
        i < BAR_COUNT;
        i++
      ) {
        const envelope =
          0.48 +
          0.16 * Math.sin(i * 0.105 + 0.8) +
          0.11 * Math.sin(i * 0.041 + 2.2);

        const texture =
          0.17 * Math.sin(i * 1.73 + 0.4) +
          0.12 * Math.sin(i * 0.91 + 1.7) +
          0.08 * Math.sin(i * 2.67 + 0.2);

        const transientA =
          Math.exp(
            -Math.pow((i - 19) / 4.5, 2)
          ) * 0.30;

        const transientB =
          Math.exp(
            -Math.pow((i - 52) / 6.5, 2)
          ) * 0.24;

        const transientC =
          Math.exp(
            -Math.pow((i - 88) / 5.0, 2)
          ) * 0.34;

        const quietPocket =
          Math.exp(
            -Math.pow((i - 70) / 8.0, 2)
          ) * 0.18;

        let value =
          envelope +
          texture +
          transientA +
          transientB +
          transientC -
          quietPocket;

        value =
          Math.max(
            0.12,
            Math.min(value, 0.98)
          );

        data.push(value);
      }

      return data;
    }


    /* =========================================================
       DRAW WAVEFORM
       ========================================================= */

    function drawWaveform(data) {
      svg.innerHTML = '';
      bars = [];

      const count = data.length;
      const spacing =
        SVG_WIDTH / count;

      const barWidth =
        Math.max(
          3,
          spacing * 0.42
        );

      data.forEach(function (
        value,
        index
      ) {
        const height =
          Math.max(
            5,
            value * 40
          );

        const x =
          index * spacing +
          (spacing - barWidth) / 2;

        const y =
          SVG_CENTER -
          height / 2;

        const bar =
          document.createElementNS(
            'http://www.w3.org/2000/svg',
            'rect'
          );

        bar.setAttribute('x', x);
        bar.setAttribute('y', y);
        bar.setAttribute(
          'width',
          barWidth
        );
        bar.setAttribute(
          'height',
          height
        );
        bar.setAttribute(
          'rx',
          barWidth / 2
        );

        bar.style.fill =
          'var(--ocular-wave)';

        svg.appendChild(bar);
        bars.push(bar);
      });

      updateProgress();
    }


    /* =========================================================
       WAVEFORM CACHE
       ========================================================= */

    function getCacheKey(url) {
      return (
        CACHE_VERSION +
        ':' +
        url
      );
    }


    function getCachedWaveform(url) {
      try {
        const cached =
          localStorage.getItem(
            getCacheKey(url)
          );

        if (!cached) {
          return null;
        }

        const parsed =
          JSON.parse(cached);

        if (
          !Array.isArray(parsed) ||
          parsed.length !== BAR_COUNT
        ) {
          return null;
        }

        return parsed;

      } catch (error) {
        return null;
      }
    }


    function cacheWaveform(
      url,
      data
    ) {
      try {
        localStorage.setItem(
          getCacheKey(url),
          JSON.stringify(data)
        );
      } catch (error) {
        /*
          Cache failure should never break playback.
        */
      }
    }


    /* =========================================================
       ANALYSE AUDIO
       ========================================================= */

    function analyseAudioBuffer(buffer) {
      const channels =
        buffer.numberOfChannels;

      const totalSamples =
        buffer.length;

      const samplesPerBar =
        Math.floor(
          totalSamples /
          BAR_COUNT
        );

      const rawValues = [];


      for (
        let barIndex = 0;
        barIndex < BAR_COUNT;
        barIndex++
      ) {
        const start =
          barIndex *
          samplesPerBar;

        const end =
          barIndex === BAR_COUNT - 1
            ? totalSamples
            : start + samplesPerBar;

        const sampleCount =
          end - start;

        const stride =
          Math.max(
            1,
            Math.floor(
              sampleCount / 1200
            )
          );

        let sumSquares = 0;
        let measuredSamples = 0;


        for (
          let sampleIndex = start;
          sampleIndex < end;
          sampleIndex += stride
        ) {
          let mixedSample = 0;

          for (
            let channel = 0;
            channel < channels;
            channel++
          ) {
            mixedSample +=
              buffer
                .getChannelData(channel)
                [sampleIndex];
          }

          mixedSample /= channels;

          sumSquares +=
            mixedSample *
            mixedSample;

          measuredSamples++;
        }


        const rms =
          measuredSamples
            ? Math.sqrt(
                sumSquares /
                measuredSamples
              )
            : 0;

        rawValues.push(rms);
      }


      const sorted =
        rawValues
          .slice()
          .sort(function (a, b) {
            return a - b;
          });


      const percentileIndex =
        Math.min(
          sorted.length - 1,
          Math.floor(
            sorted.length * 0.95
          )
        );


      const referenceLevel =
        sorted[percentileIndex] || 1;


      return rawValues.map(
        function (value) {
          let normalized =
            value /
            referenceLevel;

          normalized =
            Math.min(
              normalized,
              1
            );

          normalized =
            Math.pow(
              normalized,
              0.65
            );

          return Math.max(
            0.10,
            normalized
          );
        }
      );
    }


    /* =========================================================
       GENERATE REAL WAVEFORM

       This can download/decode the MP3, so it is never called
       on an uncached initial page load.
       ========================================================= */

    async function generateWaveform(
      url,
      requestId
    ) {
      const cached =
        getCachedWaveform(url);

      if (cached) {
        if (
          requestId ===
          analysisRequestId
        ) {
          drawWaveform(cached);
        }

        return;
      }


      try {
        const response =
          await fetch(
            url,
            {
              mode: 'cors',
              cache: 'force-cache'
            }
          );

        if (!response.ok) {
          throw new Error(
            'Audio request failed.'
          );
        }


        const arrayBuffer =
          await response.arrayBuffer();


        const AudioContextClass =
          window.AudioContext ||
          window.webkitAudioContext;

        if (!AudioContextClass) {
          throw new Error(
            'Web Audio API unavailable.'
          );
        }


        const audioContext =
          new AudioContextClass();


        const decodedAudio =
          await audioContext
            .decodeAudioData(
              arrayBuffer
            );


        const waveformData =
          analyseAudioBuffer(
            decodedAudio
          );


        cacheWaveform(
          url,
          waveformData
        );


        if (
          requestId ===
          analysisRequestId
        ) {
          drawWaveform(
            waveformData
          );
        }


        if (
          audioContext.state !==
          'closed'
        ) {
          audioContext
            .close()
            .catch(function () {});
        }

      } catch (error) {
        console.warn(
          'Ocular waveform generation failed:',
          error
        );
      }
    }


    /* =========================================================
       PLAYLIST STATE
       ========================================================= */

    function updatePlaylistState() {
      tracks.forEach(function (
        track,
        index
      ) {
        track.classList.toggle(
          'is-active',
          index === activeIndex
        );

        track.classList.toggle(
          'is-playing',
          index === activeIndex &&
          !audio.paused
        );
      });
    }


    /* =========================================================
       PLAY STATE
       ========================================================= */

    function updatePlayState() {
      const playing =
        !audio.paused;

      if (playIcon) {
        playIcon.hidden =
          playing;
      }

      if (pauseIcon) {
        pauseIcon.hidden =
          !playing;
      }

      if (playButton) {
        playButton.setAttribute(
          'aria-label',
          playing
            ? 'Pause audio'
            : 'Play audio'
        );
      }

      updatePlaylistState();
    }


    /* =========================================================
       PROGRESS
       ========================================================= */

    function updateProgress() {
      const duration =
        audio.duration || 0;

      const progress =
        duration
          ? audio.currentTime /
            duration
          : 0;


      if (currentTimeEl) {
        currentTimeEl.textContent =
          formatTime(
            audio.currentTime
          );
      }


      bars.forEach(function (
        bar,
        index
      ) {
        const position =
          index /
          bars.length;

        bar.style.fill =
          position <= progress
            ? 'var(--ocular-wave-active)'
            : 'var(--ocular-wave)';
      });


      waveform.setAttribute(
        'aria-valuenow',
        Math.round(
          progress * 100
        )
      );
    }


    /* =========================================================
       DURATION
       ========================================================= */

    function updateDuration() {
      if (durationEl) {
        durationEl.textContent =
          formatTime(
            audio.duration
          );
      }


      const activeTrack =
        tracks[activeIndex];

      if (!activeTrack) {
        return;
      }


      const durationNode =
        activeTrack.querySelector(
          '[data-track-duration]'
        );

      if (durationNode) {
        durationNode.textContent =
          formatTime(
            audio.duration
          );
      }
    }


    /* =========================================================
       ACTIVE TRACK AUTO-SCROLL
       ========================================================= */

    function ensureActiveTrackVisible() {
      if (
        !playlist ||
        isSingleTrack
      ) {
        return;
      }


      const activeTrack =
        tracks[activeIndex];

      if (!activeTrack) {
        return;
      }


      if (
        playlist.scrollHeight <=
        playlist.clientHeight + 1
      ) {
        return;
      }


      const trackTop =
        activeTrack.offsetTop -
        playlist.offsetTop;

      const trackBottom =
        trackTop +
        activeTrack.offsetHeight;

      const visibleTop =
        playlist.scrollTop;

      const visibleBottom =
        visibleTop +
        playlist.clientHeight;


      if (
        trackTop <
        visibleTop
      ) {
        playlist.scrollTo({
          top:
            Math.max(
              0,
              trackTop - 10
            ),
          behavior: 'smooth'
        });

      } else if (
        trackBottom >
        visibleBottom
      ) {
        playlist.scrollTo({
          top:
            trackBottom -
            playlist.clientHeight +
            10,
          behavior: 'smooth'
        });
      }
    }


    /* =========================================================
       ACTIVE ARTWORK
       ========================================================= */

    function updateArtwork(
      artwork,
      title
    ) {
      if (
        artwork &&
        activeArtwork &&
        activeArtworkWrap
      ) {
        player.classList.remove(
          'is-no-artwork'
        );

        activeArtwork.classList.remove(
          'is-square'
        );

        activeArtwork.src =
          artwork;

        activeArtwork.alt =
          title || '';

        activeArtworkWrap.style.display =
          '';

        applyArtworkAspectClass(
          activeArtwork
        );

      } else {
        player.classList.add(
          'is-no-artwork'
        );

        if (activeArtworkWrap) {
          activeArtworkWrap.style.display =
            'none';
        }

        if (activeArtwork) {
          activeArtwork.removeAttribute(
            'src'
          );

          activeArtwork.alt = '';
        }
      }
    }


    /* =========================================================
       AUDIO ACTIVATION

       Only called after interaction or automatic continuation.
       Until this runs, audio has no src.
       ========================================================= */

    function activateAudioForCurrentTrack() {
      const track =
        tracks[activeIndex];

      if (!track) {
        return '';
      }

      const audioUrl =
        track.dataset.audioUrl || '';

      if (!audioUrl) {
        return '';
      }


      if (loadedAudioUrl !== audioUrl) {
        audio.pause();

        audio.src =
          audioUrl;

        loadedAudioUrl =
          audioUrl;

        audio.load();
      }


      const thisRequest =
        analysisRequestId;


      generateWaveform(
        audioUrl,
        thisRequest
      );


      return audioUrl;
    }


    /* =========================================================
       ONLY ONE OCULAR PLAYER AT A TIME
       ========================================================= */

    function pauseOtherOcularPlayers() {
      document
        .querySelectorAll(
          '[data-ocular-playlist] audio'
        )
        .forEach(
          function (otherAudio) {
            if (
              otherAudio !== audio
            ) {
              otherAudio.pause();
            }
          }
        );
    }


    function playCurrentTrack() {
      pauseOtherOcularPlayers();

      const audioUrl =
        activateAudioForCurrentTrack();

      if (!audioUrl) {
        return;
      }

      audio
        .play()
        .catch(function (error) {
          console.warn(
            'Audio playback failed:',
            error
          );
        });
    }


    /* =========================================================
       LOAD TRACK UI

       Selecting a track updates UI only unless autoplay = true.

       Therefore initial load can select track 1 without loading
       its MP3.
       ========================================================= */

    function loadTrack(
      index,
      autoplay
    ) {
      if (!tracks.length) {
        return;
      }


      /*
        Manual previous/next may wrap.
        Auto-next at the end is handled separately.
      */

      if (index < 0) {
        index =
          tracks.length - 1;
      }

      if (
        index >=
        tracks.length
      ) {
        index = 0;
      }


      /*
        Stop/remove an existing source when moving to another
        selected track. The newly selected track stays dormant
        until interaction.
      */

      audio.pause();

      if (loadedAudioUrl) {
        audio.removeAttribute('src');
        loadedAudioUrl = '';
        audio.load();
      }


      const track =
        tracks[index];

      activeIndex =
        index;


      const title =
        track.dataset.title || '';

      const nowSubtitle =
        track.dataset.nowSubtitle || '';

      const artwork =
        track.dataset.artwork || '';

      const audioUrl =
        track.dataset.audioUrl || '';


      if (activeTitle) {
        activeTitle.textContent =
          title;
      }


      if (activeSubtitle) {
        activeSubtitle.textContent =
          nowSubtitle;

        activeSubtitle.style.display =
          nowSubtitle
            ? ''
            : 'none';
      }


      updateArtwork(
        artwork,
        title
      );


      /*
        If this visitor already has the real waveform cached,
        show it immediately without touching the MP3.

        Otherwise show our realistic placeholder.
      */

      const cachedWaveform =
        audioUrl
          ? getCachedWaveform(audioUrl)
          : null;

      if (cachedWaveform) {
        drawWaveform(
          cachedWaveform
        );
      } else {
        drawWaveform(
          createPlaceholderWaveform()
        );
      }


      if (currentTimeEl) {
        currentTimeEl.textContent =
          '0:00';
      }

      if (durationEl) {
        durationEl.textContent =
          '0:00';
      }


      analysisRequestId++;


      updatePlaylistState();

      ensureActiveTrackVisible();


      if (autoplay) {
        playCurrentTrack();
      }
    }


    /* =========================================================
       PLAY / PAUSE
       ========================================================= */

    if (playButton) {
      playButton.addEventListener(
        'click',
        function () {
          if (
            audio.paused ||
            !loadedAudioUrl
          ) {
            playCurrentTrack();
          } else {
            audio.pause();
          }
        }
      );
    }


    /* =========================================================
       PREVIOUS
       ========================================================= */

    if (previousButton) {
      previousButton.addEventListener(
        'click',
        function () {
          loadTrack(
            activeIndex - 1,
            true
          );
        }
      );
    }


    /* =========================================================
       NEXT
       ========================================================= */

    if (nextButton) {
      nextButton.addEventListener(
        'click',
        function () {
          loadTrack(
            activeIndex + 1,
            true
          );
        }
      );
    }


    /* =========================================================
       PLAYLIST CLICKS
       ========================================================= */

    tracks.forEach(function (
      track,
      index
    ) {
      track.addEventListener(
        'click',
        function () {

          /*
            Clicking the current track toggles play/pause.
            On first interaction it activates the MP3 first.
          */

          if (
            index ===
            activeIndex
          ) {
            if (
              audio.paused ||
              !loadedAudioUrl
            ) {
              playCurrentTrack();
            } else {
              audio.pause();
            }

            return;
          }


          loadTrack(
            index,
            true
          );
        }
      );
    });


    /* =========================================================
       SEEK

       Seeking an untouched waveform counts as interaction.
       Only then do we activate that track's audio.
       ========================================================= */

    function calculateSeekPosition(clientX) {
      const rect =
        waveform
          .getBoundingClientRect();

      return Math.min(
        Math.max(
          (
            clientX -
            rect.left
          ) /
          rect.width,
          0
        ),
        1
      );
    }


    function seekToPosition(position) {
      if (
        !Number.isFinite(
          audio.duration
        )
      ) {
        return;
      }

      audio.currentTime =
        position *
        audio.duration;

      updateProgress();
    }


    function seek(clientX) {
      const position =
        calculateSeekPosition(
          clientX
        );


      if (
        Number.isFinite(
          audio.duration
        ) &&
        loadedAudioUrl
      ) {
        seekToPosition(position);
        return;
      }


      const audioUrl =
        activateAudioForCurrentTrack();

      if (!audioUrl) {
        return;
      }


      if (
        Number.isFinite(
          audio.duration
        )
      ) {
        seekToPosition(position);
        return;
      }


      audio.addEventListener(
        'loadedmetadata',
        function seekAfterMetadata() {
          seekToPosition(position);
        },
        { once: true }
      );
    }


    waveform.addEventListener(
      'pointerdown',
      function (event) {
        seek(
          event.clientX
        );
      }
    );


    /* =========================================================
       KEYBOARD SEEKING
       ========================================================= */

    waveform.addEventListener(
      'keydown',
      function (event) {
        if (
          event.key !==
            'ArrowRight' &&
          event.key !==
            'ArrowLeft'
        ) {
          return;
        }


        event.preventDefault();


        function performKeyboardSeek() {
          if (
            !Number.isFinite(
              audio.duration
            )
          ) {
            return;
          }


          const step =
            Math.max(
              audio.duration * 0.025,
              2
            );


          if (
            event.key ===
            'ArrowRight'
          ) {
            audio.currentTime =
              Math.min(
                audio.currentTime + step,
                audio.duration
              );
          }


          if (
            event.key ===
            'ArrowLeft'
          ) {
            audio.currentTime =
              Math.max(
                audio.currentTime - step,
                0
              );
          }


          updateProgress();
        }


        if (
          loadedAudioUrl &&
          Number.isFinite(
            audio.duration
          )
        ) {
          performKeyboardSeek();
          return;
        }


        const audioUrl =
          activateAudioForCurrentTrack();

        if (!audioUrl) {
          return;
        }


        if (
          Number.isFinite(
            audio.duration
          )
        ) {
          performKeyboardSeek();
          return;
        }


        audio.addEventListener(
          'loadedmetadata',
          performKeyboardSeek,
          { once: true }
        );
      }
    );


    /* =========================================================
       LOOP
       ========================================================= */

    if (loopButton) {
      loopButton.addEventListener(
        'click',
        function () {
          audio.loop =
            !audio.loop;


          loopButton.classList.toggle(
            'is-active',
            audio.loop
          );


          loopButton.setAttribute(
            'aria-pressed',
            audio.loop
              ? 'true'
              : 'false'
          );
        }
      );
    }


    /* =========================================================
       MUTE / UNMUTE

       Intentionally NO volume slider.
       ========================================================= */

    if (volumeButton) {
      volumeButton.addEventListener(
        'click',
        function () {
          audio.muted =
            !audio.muted;


          if (volumeOn) {
            volumeOn.hidden =
              audio.muted;
          }


          if (volumeOff) {
            volumeOff.hidden =
              !audio.muted;
          }


          volumeButton.setAttribute(
            'aria-label',
            audio.muted
              ? 'Unmute audio'
              : 'Mute audio'
          );
        }
      );
    }


    /* =========================================================
       AUDIO EVENTS
       ========================================================= */

    audio.addEventListener(
      'play',
      updatePlayState
    );


    audio.addEventListener(
      'pause',
      updatePlayState
    );


    audio.addEventListener(
      'loadedmetadata',
      function () {
        updateDuration();
        updateProgress();
      }
    );


    audio.addEventListener(
      'durationchange',
      updateDuration
    );


    audio.addEventListener(
      'timeupdate',
      updateProgress
    );


    /* =========================================================
       AUTO-NEXT

       Loop = browser repeats current track.

       Otherwise:
       - advance if another track exists
       - final track stops
       - never auto-wrap to track 1
       ========================================================= */

    audio.addEventListener(
      'ended',
      function () {
        if (audio.loop) {
          return;
        }


        const nextIndex =
          activeIndex + 1;


        if (
          nextIndex <
          tracks.length
        ) {
          loadTrack(
            nextIndex,
            true
          );

        } else {
          /*
            Final track finished.
            Leave it selected and stopped.
          */

          audio.currentTime = 0;

          updateProgress();
          updatePlayState();
        }
      }
    );


    /* =========================================================
       INITIAL LOAD

       UI ONLY.

       This intentionally does NOT:
       - assign audio.src
       - call audio.load()
       - fetch an MP3
       - decode audio
       - generate an uncached real waveform
       ========================================================= */

    loadTrack(
      0,
      false
    );
  }


  /* =========================================================
     INITIALIZE ALL PLAYERS
     ========================================================= */

  function initializeAllOcularPlayers(
    root
  ) {
    const scope =
      root || document;


    if (
      scope.matches &&
      scope.matches(
        '[data-ocular-playlist]'
      )
    ) {
      initializeOcularPlayer(
        scope
      );
    }


    if (
      scope.querySelectorAll
    ) {
      scope
        .querySelectorAll(
          '[data-ocular-playlist]'
        )
        .forEach(
          initializeOcularPlayer
        );
    }
  }


  /* =========================================================
     DESTROY BEFORE SHOPIFY RE-RENDERS

     This matters inside Theme Editor.
     ========================================================= */

  function destroyOcularPlayers(root) {
    if (!root) return;

    const players = [];


    if (
      root.matches &&
      root.matches(
        '[data-ocular-playlist]'
      )
    ) {
      players.push(root);
    }


    if (
      root.querySelectorAll
    ) {
      root
        .querySelectorAll(
          '[data-ocular-playlist]'
        )
        .forEach(
          function (player) {
            players.push(player);
          }
        );
    }


    players.forEach(
      function (player) {
        const audio =
          player.querySelector(
            '[data-audio]'
          );

        if (audio) {
          audio.pause();
          audio.removeAttribute('src');
          audio.load();
        }

        delete player.dataset.initialized;
      }
    );
  }


  /* =========================================================
     INITIAL PAGE LOAD
     ========================================================= */

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        initializeAllOcularPlayers(
          document
        );
      },
      { once: true }
    );

  } else {
    initializeAllOcularPlayers(
      document
    );
  }


  /* =========================================================
     SHOPIFY THEME EDITOR

     Reinitialize after section rendering.
     ========================================================= */

  document.addEventListener(
    'shopify:section:unload',
    function (event) {
      destroyOcularPlayers(
        event.target
      );
    }
  );


  document.addEventListener(
    'shopify:section:load',
    function (event) {
      initializeAllOcularPlayers(
        event.target
      );
    }
  );


  /*
    Some editor changes replace/re-render
    blocks without a traditional page load.

    A MutationObserver catches newly inserted
    Ocular players and initializes them.
  */

  const observer =
    new MutationObserver(
      function (mutations) {
        mutations.forEach(
          function (mutation) {
            mutation.addedNodes.forEach(
              function (node) {
                if (
                  node.nodeType !== 1
                ) {
                  return;
                }

                initializeAllOcularPlayers(
                  node
                );
              }
            );
          }
        );
      }
    );


  observer.observe(
    document.documentElement,
    {
      childList: true,
      subtree: true
    }
  );

})();